<!-- src/views/CardStatusList.vue (or similar path) -->
<template> 
  <!-- Assuming Breadcrumbs handles opening -->
  <div class="container-fluid"> 
    <div class="row starter-main mt-5 pt-5"> 
      <TableTitle
        :title="title"
        :pageTitle="pageTitle"
        icon="fas fa-book"
        :showAction="true"
        actionLabel="Create New"
        :modal="'createNew'"
        :disabled="!canCreate('data')"
      />
      <CommonTable2
        :tableObj="tableConfig" 
        @edit="handleEdit"
        @delete="handleDelete"
        @view="handleView"
        permissionKey="data"
      />
    </div>
  </div>

   <!-- pagination -->
  <div class="d-flex justify-content-end align-items-center gap-3 mt-3 mb-4">
    <!-- Rows per page -->
    <div class="d-flex align-items-center gap-2">
      <span class="text-muted">Rows per page:</span>
      <select
        class="form-select form-select-sm rows-select"
        v-model="tablePagination.per_page"
        @change="changeRows"
      >
        <option v-for="n in [5, 10, 20, 50, 100]" :key="n" :value="n">
          {{ n }}
        </option>
      </select>
    </div>
    <!-- Pagination Buttons -->
    <div class="pagination-mini">
      <!-- Previous -->
      <button
        class="page-btn prev_btn"
        :disabled="!tablePagination.has_prev"
        @click="handlePageChange(tablePagination.current_page - 1)"
      >
        «
      </button>
      <!-- Numbered Pages -->
      <button
        v-for="(p, index) in visiblePages"
        :key="index"
        class="page-btn"
        :class="{
          'page-active': p === tablePagination.current_page,
          'disabled-dots': p === '...',
        }"
        :disabled="p === '...'"
        @click="p !== '...' && handlePageChange(p)"
      >
        {{ p }}
      </button>

      <!-- Next -->
      <button
        class="page-btn next_btn"
        :disabled="!tablePagination.has_next"
        @click="handlePageChange(tablePagination.current_page + 1)"
      >
        »
      </button>
    </div>
  </div>

  <BaseModal
    id="createNew"
    :title="`Create ${pageTitle}`"
    :visible="isCreateModalOpen"
    @close="closeCreateModal"
    @save="handleSaveCreate"
    :save-button-disabled="isCreateFormValid"
  >
    <template #body>
      <!-- Crucial: v-model binds newItemData to BaseForm -->
      <BaseForm
        :fields="createFormFields"
        v-model="newItemData"
        @submit="handleSaveCreate"
        ref="createFormRef"
      />
    </template>
  </BaseModal>

  <!-- Edit/View Modal -->
  <BaseModal
    id="editModal"
    :title="`${isViewMode ? 'View' : 'Edit'} ${pageTitle}`"
    :visible="isEditModalOpen"
    @close="closeEditModal"
    @save="handleSaveEdit"
    :show-save-button="!isViewMode"
    :save-button-disabled="isEditFormValid"
  >
    <template #body>
      <!-- Crucial: v-model binds editingItemData to BaseForm -->
      <BaseForm
        :fields="editFormFields"
        v-model="editingItemData"
        @submit="handleSaveEdit"
        ref="editFormRef"
      />
    </template>
  </BaseModal>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch, nextTick } from "vue";
import { useCrud } from "@/composables/commen/useCrud"; // Adjust path
import BaseModal from "@/components/commen/modal/BaseModal.vue"; // Adjust path
import BaseForm from "@/components/commen/modal/BaseForm.vue"; // Adjust path 
import TableTitle from "@/components/commen/table/TableTitle.vue";
import CommonTable2 from "@/components/commen/table/CommonTable2.vue";
import { usePermissions } from "@/composables/usePermissions";
const { canCreate } = usePermissions();
const API_RESOURCE = "/common/marketplace-categories";
const pageTitle = "Marketplace Category";
const title = "Marketplace Categories ";

// Use the generic CRUD composable
const crud = useCrud(API_RESOURCE);

// pagination local UI state (mirrors backend, but we keep per_page from UI)
const tablePagination = ref({
  current_page: 1,
  per_page: 10,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
});

const currentPage = ref(1);

// visiblePages computed (same advanced logic)
const visiblePages = computed(() => {
  const total = tablePagination.value.total_pages;
  const current = tablePagination.value.current_page;

  if (!total || total <= 4) {
    return Array.from({ length: Math.max(1, total) }, (_, i) => i + 1);
  }

  const pages: (number | string)[] = [];
  pages.push(1);

  if (current > 4) pages.push("...");

  const windowStart = Math.max(2, current - 1);
  const windowEnd = Math.min(total - 1, current + 1);
  for (let i = windowStart; i <= windowEnd; i++) pages.push(i);

  if (current < total - 3) pages.push("...");

  pages.push(total);
  return pages;
});

 
// Table configuration
const rows = ref([]);
const tableConfig = ref({
  columns: [
    { name: "Title", field: "title", sortable: true },
    // { name: "Color", field: "color", sortable: true },
  ],
  data: rows, // keep as a ref
  actions: [true],
  loading: crud.isLoading, 
  showStatusFilter: false,
  table_width: "max-content", 
});
watch(crud.data, (newVal) => {
  tableConfig.value.data = newVal;
});
watch(crud.isLoading, (newVal) => {
  tableConfig.value.loading = newVal;
});
 
// Sync backend pagination into tablePagination, but DO NOT overwrite UI per_page except if UI didn't set it.
watch(
  () => crud.pagination.value,
  (newVal) => {
    if (!newVal) return;

    tablePagination.value.current_page = newVal.current_page || 1;
    tablePagination.value.total_pages = newVal.total_pages || 1;
    tablePagination.value.total = newVal.total || 0;

    // keep UI rows per page unless backend changed
    tablePagination.value.per_page = tablePagination.value.per_page;
    tablePagination.value.has_prev = tablePagination.value.current_page > 1;
    tablePagination.value.has_next = tablePagination.value.current_page < tablePagination.value.total_pages;
  },
  { immediate: true, deep: true }
);

// === ACTIONS ===
const changeRows = async () => {
  // when user changes rows, reset to page 1 and fetch
  currentPage.value = 1;
  await crud.fetchData({ page: 1, limit: tablePagination.value.per_page });
};

const handlePageChange = async (page: number) => {
  // protect bounds
  if (page < 1 || page === tablePagination.value.current_page) return;
  if (
    tablePagination.value.total_pages &&
    page > tablePagination.value.total_pages
  )
    return;

  currentPage.value = page;
  await crud.fetchData({ page, limit: tablePagination.value.per_page });
};

// --- Modal State ---
const isCreateModalOpen = ref(false);
const isEditModalOpen = ref(false);
const isViewMode = ref(false);

// --- Form Data ---
const newItemData = ref({ title: "", slug: "" }); // Initial create data
const editingItemData = ref({ _id: "", title: "", slug: "" }); // Initial edit/view data

// --- Form Definitions ---
const createFormFields = [
  {
    key: "title",
    label: "Title",
    type: "text",
    required: true,
    placeholder: "Enter title",
  },
   {
    key: "slug",
    label: "Slug",
    type: "text",
    required: true,
    placeholder: "Enter slug",
  }
];

const editFormFields = computed(() => [
  {
    key: "title",
    label: "Title",
    type: "text",
    required: true,
    placeholder: "Enter title",
    readonly: isViewMode.value,
  },
    {
    key: "slug",
    label: "Slug",
    type: "text",
    required: true,
    placeholder: "Enter slug",
    readonly: isViewMode.value,
  }
]);

// --- Form Validation (Example) ---
const isCreateFormValid = computed(() =>!newItemData.value.title.trim() || !newItemData.value.slug.trim());
const isEditFormValid = computed(() => !editingItemData.value.title?.trim() || !editingItemData.value.slug?.trim());

// --- Handlers ---
const handleEdit = (row: any) => {
  isViewMode.value = false;
  editingItemData.value = { ...row }; // Populate form
  isEditModalOpen.value = true; // Open modal
};

const handleView = (row: any) => {
  isViewMode.value = true;
  editingItemData.value = { ...row }; // Populate form
  isEditModalOpen.value = true; // Open modal
};

const handleDelete = async (id: string) => {
  if (confirm("Are you sure you want to delete this item?")) {
    await crud.deleteItem(id); // Use composable function
    // No need to manually update tableConfig.data, crud.data is reactive and bound
     await crud.fetchData({
      page: tablePagination.value.current_page,
      limit: tablePagination.value.per_page,
    });
  }
};

const handleSaveCreate = async () => {
  if (isCreateFormValid.value) {
    return;
  }

  // Access form data if needed directly from ref, or pass via emit payload
  // const formData = createFormRef.value?.formData;
  const result = await crud.createItem(newItemData.value); // Use composable
  if (result) {
    // Close on success
    closeCreateModal();
    newItemData.value = { title: "", slug: "" }; // Reset form data
    await crud.fetchData({ page: 1, limit: tablePagination.value.per_page });
  }
};

const handleSaveEdit = async () => {
  const id = editingItemData.value._id;
  if (!id) return;
  const { _id, ...updatePayload } = editingItemData.value;
  const result = await crud.updateItem(id, updatePayload);
  if (result) {
    closeEditModal();
     await crud.fetchData({
      page: tablePagination.value.current_page,
      limit: tablePagination.value.per_page,
    });
  }
};

const closeCreateModal = () => {
  isCreateModalOpen.value = false;
  isCreateModalOpen.value = false;
  newItemData.value = { title: "", slug: "" }; // Reset form data
};

const closeEditModal = () => {
  isEditModalOpen.value = false;
  isViewMode.value = false;
  editingItemData.value = { _id: "", title: "", slug: "" }; // Reset form data
};

// --- lifecycle: initial load ---
onMounted(async () => {
  await crud.fetchData({
    page: tablePagination.value.current_page,
    limit: tablePagination.value.per_page,
  });
});

 

// For Create Modal - generate slug in real time
watch(() => newItemData.value.title, (val) => {
  newItemData.value.slug = val
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")    // spaces → hyphens
    .replace(/[^a-z0-9-]/g, ""); // remove invalid chars
});

</script>

<style scoped>
/* pagination style */
.rows-select {
  width: 82px;
  padding: 8px 17px;
  font-size: 0.85rem;
  background-color: transparent;
}

/* Mini Pagination */
.pagination-mini {
  display: flex;
  align-items: center;
  border-radius: 6px;
  overflow: hidden;
}

.page-btn {
  border: none;
  background: transparent;
  font-size: 1rem;
  padding: 7px 12px;
  cursor: pointer;
  color: #6c757d;
  border-radius: 0px;
  transition: 0.2s;
  border: 1px solid #dee2e6;
  outline: none;
}
.page-btn:first-child {
  border-top-left-radius: 6px;
  border-bottom-left-radius: 6px;
}
.page-btn:last-child {
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
}
.page-btn:hover {
  background: #e9ecef;
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Active page (highlight like the screenshot) */
.page-active {
  background: #21c1d6;
  color: #fff;
  padding: 7px 12px;
  font-weight: 600;
  min-width: 28px;
  text-align: center;
  display: inline-block;
  border: 1px solid #21c1d6;
}
.page-active:hover{
   background: #21c1d6;
}
.prev_btn,
.next_btn {
  color: #21c1d6;
}
</style>
